import { Entity, Property as IgnitionProperty } from '@square/ignition';
import { NativeButton } from '@squareup/dex-ui';
import { breakHtmlForCamelCaseReact, stripV1 } from '@squareup/dex-utils-text';
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';

import { useApiRferenceFieldContext } from './ApiReferenceFieldProvider';
import { ApiReferenceTypeText } from './ApiReferenceTypeText';
import styles from './api-reference-field-type.module.css';

interface Props {
  entity?: Entity | undefined;
  itemEntity?: Entity | undefined;
  entityStack?: Entity[];

  labelName?: string;

  isRequest?: boolean | undefined;
  isEntity?: boolean | undefined;
  isMap?: boolean | undefined;
  isMapEntry?: boolean | undefined;
  isArray?: boolean | undefined;
  isEnum?: boolean | undefined;

  hasEntity?: boolean | undefined;
  hasEnum?: boolean | undefined;

  type?: IgnitionProperty['type'] | undefined;
  itemType?: IgnitionProperty['itemType'] | undefined;
  format?: IgnitionProperty['format'];
}

function formatProperty(
  type: IgnitionProperty['itemType'],
  format?: IgnitionProperty['format'] | undefined
) {
  if (type === 'integer') {
    return format === 'int64' ? '(64-bit)' : '(32-bit)';
  }

  return '';
}

const ApiReferenceFieldType: FunctionComponent<Props> = ({
  entity,
  itemEntity,
  entityStack,

  labelName = 'field-type',

  isEntity,
  isRequest,
  isMap = false,
  isMapEntry = false,
  isArray = false,
  isEnum = false,

  hasEntity = false,

  type,
  itemType,
  format,
}) => {
  const referenceContext = useApiRferenceFieldContext();

  const openModalFn = useCallback(() => {
    const currentEntity = entity || itemEntity;
    if (currentEntity) {
      referenceContext.openModal(currentEntity, entityStack || []);
    }
  }, [entity, itemEntity, referenceContext, entityStack]);

  const mapName = isMapEntry ? 'Entry' : 'Map';

  function createTypeMarkup() {
    const EntityButton = ({ children }: { children: ReactNode }) => (
      <NativeButton
        aria-label={`Open entity details modal for ${labelName}`}
        testId={`property-type-entity-modal-button-${labelName}`}
        trackingId={`open-entity-${labelName}-property-modal`}
        className={styles['type-button']}
        onClick={openModalFn}
      >
        <ApiReferenceTypeText>{children}</ApiReferenceTypeText>
      </NativeButton>
    );

    if ((isEntity || isRequest) && entity) {
      return (
        <EntityButton>
          {breakHtmlForCamelCaseReact(stripV1(entity.name))}
        </EntityButton>
      );
    } else if (isArray) {
      if ((hasEntity || itemEntity?.isEnum) && itemEntity) {
        return (
          <EntityButton>
            {breakHtmlForCamelCaseReact(stripV1(itemEntity.name))}
            &nbsp;[ ]
          </EntityButton>
        );
      } else {
        return <ApiReferenceTypeText>{itemType} [ ]</ApiReferenceTypeText>;
      }
    } else if (isMap) {
      if ((hasEntity || itemEntity?.isEnum) && itemEntity) {
        return (
          <EntityButton>
            {`${mapName}<string, `}
            {breakHtmlForCamelCaseReact(stripV1(itemEntity.name))}
            {'>'}
          </EntityButton>
        );
      } else {
        return (
          <ApiReferenceTypeText>{`${mapName}<string, ${itemType}>`}</ApiReferenceTypeText>
        );
      }
    } else if (isEnum) {
      return <ApiReferenceTypeText>string</ApiReferenceTypeText>;
    } else {
      return (
        <ApiReferenceTypeText>
          {type}
          {formatProperty(type, format)}
        </ApiReferenceTypeText>
      );
    }
  }

  const typeMarkup = useMemo(createTypeMarkup, [
    isEntity,
    isRequest,
    entity,
    isArray,
    isMap,
    isEnum,
    labelName,
    openModalFn,
    hasEntity,
    itemEntity,
    itemType,
    mapName,
    type,
    format,
  ]);

  return typeMarkup;
};

export { ApiReferenceFieldType };
